import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis } from "recharts";

import { useDateRangeStore } from "./use-date-range-store";
import { fetchLeadsDateWiseOfAllEmployeeForAdmin } from "@/config/queries/admin/admin-queries";
import { Report1DateRangePicker } from "./date-range-picker";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart/chart";

function adjustDates(
  d1: Date,
  d2: Date
): { adjustedD1: Date | string; adjustedD2: Date | string } {
  // Create new Date objects to avoid mutating the original inputs
  const adjustedD1 = new Date(d1);
  const adjustedD2 = new Date(d2);

  // Set d1 time to 00:00:00
  adjustedD1.setUTCHours(0, 0, 0, 0);
  adjustedD1.setUTCDate(adjustedD1.getUTCDate() + 1);

  // Set d2 time to 00:00:00 and add 1 day
  adjustedD2.setUTCHours(0, 0, 0, 0);
  adjustedD2.setUTCDate(adjustedD2.getUTCDate() + 1);

  // Return the adjusted dates
  return { adjustedD1, adjustedD2 };
}

function adjustDatesFirstTime(
  d1: Date,
  d2: Date
): { adjustedD1: Date | string; adjustedD2: Date | string } {
  // Create new Date objects to avoid mutating the original inputs
  const adjustedD1 = new Date(d1);
  const adjustedD2 = new Date(d2);

  // Set d1 time to 00:00:00
  adjustedD1.setUTCHours(0, 0, 0, 0);

  // Set d2 time to 00:00:00 and add 1 day
  adjustedD2.setUTCHours(0, 0, 0, 0);
  adjustedD2.setUTCDate(adjustedD2.getUTCDate() + 1);

  // Return the adjusted dates
  return { adjustedD1, adjustedD2 };
}

export const LeadReport2 = () => {
  const key = Cookies.get("key") as string;

  const [firstTime, setFirstTime] = useState(true);

  const { dateRange } = useDateRangeStore();

  const startdate = dateRange?.from as Date;
  const enddate = dateRange?.to as Date;

  const adjusedDates = useMemo(() => {
    const { adjustedD1, adjustedD2 } = adjustDates(startdate, enddate);

    return {
      startdate: adjustedD1,
      enddate: adjustedD2,
    };
  }, [startdate, enddate]);

  const adjusedDatesFirstTime = useMemo(() => {
    const { adjustedD1, adjustedD2 } = adjustDatesFirstTime(startdate, enddate);

    return {
      startdate: adjustedD1,
      enddate: adjustedD2,
    };
  }, [startdate, enddate]);

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["datewise-leadsss", startdate, enddate],
    queryFn: () =>
      fetchLeadsDateWiseOfAllEmployeeForAdmin(key)({
        startdate: firstTime
          ? (adjusedDatesFirstTime.startdate as Date)
          : (adjusedDates.startdate as Date),
        enddate: firstTime
          ? (adjusedDatesFirstTime.enddate as Date)
          : (adjusedDates.enddate as Date),
      }),
    refetchOnWindowFocus: false,
  });

  const datewiseAllEmpData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.reports === undefined) {
      return [];
    }

    const result = data?.reports?.map((report) => {
      let resultant = {
        employee: "",
        alloted: 0,
        attempted: 0,
        open: 0,
        failed: 0,
        success: 0,
      };

      for (const key in report) {
        const empData = report[key];
        resultant.employee = key;
        resultant.alloted = empData.alloted;
        resultant.attempted = empData.attempted;
        resultant.open = empData.open;
        resultant.failed = empData.closed.failed;
        resultant.success = empData.closed.success;
      }

      return resultant;
    });

    const filtered = result?.filter((res) => res.alloted !== 0);

    return filtered;
  }, [data, isPending, isFetching]);

  const chartConfig = {
    alloted: {
      label: "Alloted",
      color: "hsl(var(--chart-5))",
    },
    open: {
      label: "Open",
      color: "hsl(var(--chart-4))",
    },
    attempted: {
      label: "Attempted",
      color: "hsl(var(--chart-1))",
    },
    success: {
      label: "Success",
      color: "hsl(var(--chart-2))",
    },
    failed: {
      label: "Failed",
      color: "hsl(var(--chart-3))",
    },
  } satisfies ChartConfig;

  return (
    <div className="p-1 pb-3 border-b mt-5">
      <div className="space-y-2">
        <div className="text-xl font-medium">Report 2</div>
        <div className="text-sm text-muted-foreground">
          This report is a graphical representation of number of assigned leads
          (alloted), number of attempted leads (attempted), number of leads
          closed successfully (success), number of leads marked as "Not
          Interested" (failed) for a day or a given range of dates by employees.
        </div>
      </div>

      <div className="pt-6">
        <div className="flex gap-5">
          <div>
            <Report1DateRangePicker
              firstTime={firstTime}
              firstTimeChange={() => {
                setFirstTime(false);
              }}
            />
          </div>
        </div>

        <div className="mt-4">
          <ChartContainer config={chartConfig} className="h-[380px] w-full">
            <BarChart
              accessibilityLayer
              data={datewiseAllEmpData}
              margin={{
                left: 12,
                right: 12,
                top: 30,
                bottom: 22,
              }}
              barGap={62}
              maxBarSize={10}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="employee"
                tickLine={false}
                tickMargin={50}
                axisLine={false}
                tickFormatter={(value) => value.slice(0, 15)}
              />
              <ChartTooltip
                content={<ChartTooltipContent indicator="line" />}
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar
                dataKey="alloted"
                fill="var(--color-alloted)"
                radius={[4, 4, 4, 4]}
                barSize={40}
              />
              <Bar
                dataKey="attempted"
                fill="var(--color-attempted)"
                radius={[4, 4, 4, 4]}
                barSize={40}
              />
              <Bar
                dataKey="success"
                stackId="a"
                fill="var(--color-success)"
                radius={[0, 0, 0, 0]}
                barSize={40}
              />
              <Bar
                dataKey="failed"
                stackId="a"
                fill="var(--color-failed)"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="open"
                stackId="a"
                fill="var(--color-open)"
                radius={[4, 4, 0, 0]}
                barSize={40}
              />
            </BarChart>
          </ChartContainer>
        </div>
      </div>
    </div>
  );
};
